import { ApplicationConfig, provideZoneChangeDetection, importProvidersFrom } from '@angular/core';
import { provideRouter, RouteReuseStrategy, withPreloading } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { provideEchartsCore } from 'ngx-echarts';
import { routes } from './app.routes';
import { reducers, metaReducers } from './store/app.reducer';
import { AppEffects } from './store/app.effects';
import { AuthModule } from './auth/auth.module';
import { ServerUrlInterceptor } from '@shared/services/ui-http/server-url-interceptor.service';
import { CustomRouteReuseStrategy } from './custom-route-reuse-strategy';
import { SelectivePreloadingStrategyService } from './selective-preloading-strategy.service';
import { environment } from '../environments/environment';

// Guards and Resolvers (previously in AppRoutingModule)
import { AppGuard } from './guards/app.guard';
import { DesktopResolverService } from './guards/desktop-resolver.service';
import { MobileGuard } from './guards/mobile.guard';
import { LocalesResolver } from './guards/locales.resolver';

// Services from UiSharedModule
import { UiHttp } from '@shared/services/ui-http/ui-http.service';
import { LoadingService } from '@shared/services/ui-http/loading.service';
import { UserService } from '@shared/services/user/user.service';
import { SendEmailDialogService } from '@shared/services/send-email-dialog.service';
import { SendPhoneDialogService } from '@shared/services/send-phone-dialog.service';
import { ContentEditDialogService } from '@shared/services/content-edit-dialog.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withPreloading(SelectivePreloadingStrategyService)),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),

    // NgRx Store (using importProvidersFrom for compatibility with feature modules)
    importProvidersFrom(
      StoreModule.forRoot(reducers, {
        metaReducers,
        runtimeChecks: {
          strictStateImmutability: false,
          strictActionImmutability: true,
          strictActionSerializability: true,
          strictStateSerializability: true,
          strictActionWithinNgZone: true,
          strictActionTypeUniqueness: true
        }
      }),
      EffectsModule.forRoot([AppEffects]),
      StoreDevtoolsModule.instrument({
        maxAge: 25,
        logOnly: environment.production,
        connectInZone: true
      })
    ),
    // Echarts
    provideEchartsCore({
      echarts: () => import('echarts'),
    }),
    // HTTP Interceptors
    { provide: HTTP_INTERCEPTORS, useClass: ServerUrlInterceptor, multi: true },

    // Route Reuse Strategy
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },

    // Material Config
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
    { provide: MAT_RADIO_DEFAULT_OPTIONS, useValue: { color: '#493C70' } },

    // Guards and Resolvers
    AppGuard,
    DesktopResolverService,
    MobileGuard,
    LocalesResolver,

    // Shared Services
    UiHttp,
    LoadingService,
    UserService,
    SendEmailDialogService,
    SendPhoneDialogService,
    ContentEditDialogService,

    // Legacy Modules (import their declarations and providers)
    importProvidersFrom(AuthModule),
  ]
};
